import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Clock, XCircle, Package, Landmark } from "lucide-react";

function rupee(n) { return `₹${Number(n).toLocaleString("en-IN")}`; }

const MAX_POLLS = 6;
const POLL_INTERVAL_MS = 2500;

export default function OrderDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { user } = useAuth();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payStatus, setPayStatus] = useState("checking"); // checking | paid | pending | failed | not_applicable
  const [methods, setMethods] = useState({ bank_details: [] });
  const pollsRef = useRef(0);

  useEffect(() => {
    api.get("/payments/methods").then(({ data }) => setMethods(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user || !id) return;
    api.get(`/orders/${id}`).then(({ data }) => setOrder(data)).finally(() => setLoading(false));
  }, [user, id]);

  // Poll Stripe status when returning from the hosted page.
  useEffect(() => {
    if (!user || !sessionId) { setPayStatus("not_applicable"); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/stripe/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setPayStatus("paid");
          // Refresh order to reflect new status/payment_status.
          const { data: fresh } = await api.get(`/orders/${id}`);
          setOrder(fresh);
          return;
        }
        if (data.status === "expired" || data.status === "canceled") {
          setPayStatus("failed");
          return;
        }
        pollsRef.current += 1;
        if (pollsRef.current >= MAX_POLLS) {
          setPayStatus("pending");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        console.error("Stripe status check failed:", err);
        setPayStatus("failed");
      }
    };
    setPayStatus("checking");
    poll();
    return () => { cancelled = true; };
    // MAX_POLLS / POLL_INTERVAL_MS are module-level constants and `api` is a
    // singleton — none of them change at runtime, so they intentionally stay
    // out of the dep array.
  }, [sessionId, user, id]);

  if (!user) {
    return <div className="px-6 md:px-24 py-24 text-center"><div className="font-serif-h text-3xl">Sign in to view this order</div><Link to="/login" className="btn-ink mt-6 inline-flex">Sign In</Link></div>;
  }
  if (loading) return <div className="px-6 md:px-24 py-24 text-center text-stone-500">Loading…</div>;
  if (!order) return <div className="px-6 md:px-24 py-24 text-center"><div className="font-serif-h text-3xl">Order not found</div><Link to="/orders" className="btn-ink mt-6 inline-flex">Back to Orders</Link></div>;

  // Determine banner content.
  const orderPaid = order.payment_status === "paid";
  const cod = order.payment_method === "cod";
  const bankTransfer = order.payment_method === "bank_transfer";

  return (
    <div className="px-6 md:px-12 lg:px-24 py-16 min-h-[70vh]" data-testid="order-detail-page">
      <Link to="/orders" className="text-[11px] tracking-overline uppercase text-stone-500 hover:text-[#D4AF37]">← All orders</Link>
      <h1 className="font-serif-h font-light text-4xl md:text-5xl text-stone-900 mt-4">Order #{order.id.slice(0, 8).toUpperCase()}</h1>
      <div className="text-sm text-stone-500 mt-2">
        Placed on {new Date(order.created_at).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })}
      </div>

      {/* Payment status banner */}
      <div className="mt-8" data-testid="payment-banner">
        {orderPaid && (
          <div className="flex items-start gap-4 border border-[#D4AF37] bg-[#F8EDE9]/50 p-5">
            <CheckCircle2 size={24} className="text-[#D4AF37] mt-0.5" />
            <div>
              <div className="font-medium text-stone-900">Payment received — thank you!</div>
              <div className="text-sm text-stone-600 mt-1">We'll ship your pieces within 3–5 business days.</div>
            </div>
          </div>
        )}
        {!orderPaid && payStatus === "checking" && (
          <div className="flex items-start gap-4 border border-warm bg-white p-5">
            <Clock size={24} className="text-stone-500 mt-0.5 animate-pulse" />
            <div>
              <div className="font-medium text-stone-900">Confirming your payment…</div>
              <div className="text-sm text-stone-600 mt-1">This usually takes a few seconds. Please don't close the page.</div>
            </div>
          </div>
        )}
        {!orderPaid && payStatus === "pending" && (
          <div className="flex items-start gap-4 border border-warm bg-white p-5">
            <Clock size={24} className="text-stone-500 mt-0.5" />
            <div>
              <div className="font-medium text-stone-900">Payment is still being processed.</div>
              <div className="text-sm text-stone-600 mt-1">We'll email you once it confirms. Refresh this page to retry.</div>
            </div>
          </div>
        )}
        {!orderPaid && payStatus === "failed" && (
          <div className="flex items-start gap-4 border border-[#5A1618] bg-white p-5">
            <XCircle size={24} className="text-[#5A1618] mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-stone-900">Payment was not completed.</div>
              <div className="text-sm text-stone-600 mt-1">You can retry below, or reach us on WhatsApp for a helping hand.</div>
              <div className="flex flex-wrap gap-3 mt-4">
                <Link
                  to="/checkout"
                  className="btn-ink"
                  data-testid="failed-retry-btn"
                >
                  Retry payment
                </Link>
                <a
                  href={`https://wa.me/918660515091?text=${encodeURIComponent(`Hi Riddhi 🌸 My payment for order #${order.id.slice(0, 8).toUpperCase()} didn't go through. Could you help?`)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-gold-outline"
                  data-testid="failed-whatsapp-btn"
                >
                  Chat on WhatsApp
                </a>
              </div>
            </div>
          </div>
        )}
        {!orderPaid && cod && payStatus === "not_applicable" && (
          <div className="flex items-start gap-4 border border-warm bg-white p-5">
            <Package size={24} className="text-[#D4AF37] mt-0.5" />
            <div>
              <div className="font-medium text-stone-900">Order confirmed — Cash on Delivery</div>
              <div className="text-sm text-stone-600 mt-1">Please keep {rupee(order.total)} ready when your parcel arrives.</div>
            </div>
          </div>
        )}
        {!orderPaid && bankTransfer && payStatus === "not_applicable" && methods.bank_details?.length > 0 && (
          <div className="border border-warm bg-[#FDFBF7] p-6" data-testid="bank-details-panel">
            <div className="flex items-center gap-3 mb-4">
              <Landmark size={22} className="text-[#D4AF37]" />
              <div className="font-serif-h text-xl">Please transfer {rupee(order.total)} to:</div>
            </div>
            <ul className="text-sm text-stone-700 space-y-1">
              {methods.bank_details.map((line) => <li key={line}>• {line}</li>)}
            </ul>
            <div className="text-xs text-stone-500 mt-4 leading-relaxed">
              Reference: <b>#{order.id.slice(0, 8).toUpperCase()}</b>. Once transferred, WhatsApp your payment screenshot to
              <a href="https://wa.me/918660515091" target="_blank" rel="noreferrer" className="text-[#25D366] font-medium ml-1">+91 86605 15091</a> and
              we'll ship within 3–5 business days.
            </div>
          </div>
        )}
      </div>

      {/* Items */}
      <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <h2 className="font-serif-h text-2xl mb-6">Items</h2>
          <div className="space-y-5">
            {order.items.map((it) => (
              <div key={`${it.product_id}-${it.size}`} className="flex gap-4 border border-warm p-4 bg-white">
                <img src={it.image} className="w-20 h-24 object-cover bg-[#F3EAE8]" alt="" />
                <div className="flex-1 text-sm">
                  <div className="font-medium text-stone-900">{it.name}</div>
                  <div className="text-stone-500 text-xs mt-1">Size {it.size} · Qty {it.quantity}</div>
                  <div className="mt-2 text-stone-900">{rupee(it.price * it.quantity)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="border border-warm p-6 bg-white">
            <div className="font-serif-h text-xl mb-4">Summary</div>
            <div className="flex justify-between text-sm"><span className="text-stone-600">Subtotal</span><span>{rupee(order.subtotal)}</span></div>
            <div className="flex justify-between text-sm mt-2"><span className="text-stone-600">Shipping</span><span>{order.shipping === 0 ? "Complimentary" : rupee(order.shipping)}</span></div>
            <div className="flex justify-between text-lg mt-4 pt-4 border-t border-warm"><span>Total</span><span className="font-medium">{rupee(order.total)}</span></div>

            <div className="mt-6 text-[11px] tracking-overline uppercase text-stone-500">Shipping to</div>
            <div className="text-sm text-stone-700 mt-2 leading-relaxed">
              {order.shipping_address.full_name}<br />
              {order.shipping_address.address_line1}{order.shipping_address.address_line2 ? `, ${order.shipping_address.address_line2}` : ""}<br />
              {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.pincode}<br />
              {order.shipping_address.phone}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
